import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import axiosInstance from "../api/axiosInstance";

/**
 * useFilterFacets
 *
 * Fetches dynamic filter options from POST /api/filters/facets?type=<type>
 * whenever the selected filter IDs change.
 *
 * The API accepts an object of selected IDs and responds with related
 * departments, projects, groups, and employees so that each dropdown only
 * shows values that are relevant to the current selection.
 *
 * @param {string}  type            - "my" | "deputed" (passed as ?type query param)
 * @param {object}  selectedFilters - { department_id, project_id, group_id, employee_id }
 *                                    Each value is a string ID or "" when unset.
 */
const useFilterFacets = (type = "my", selectedFilters = {}) => {
    const [facetOptions, setFacetOptions] = useState({
        departments: [],
        projects: [],
        groups: [],
        employees: [],
    });
    const [isLoadingFacets, setIsLoadingFacets] = useState(false);

    // Track the last request so we can abort stale ones
    const abortControllerRef = useRef(null);

    const fetchFacets = useCallback(async (filters) => {
        // Build the POST body — only include non-empty ID arrays
        const body = {
            department_id: filters.department_id ? [filters.department_id] : [],
            project_id: filters.project_id ? [filters.project_id] : [],
            group_id: filters.group_id ? [filters.group_id] : [],
            employee_id: filters.employee_id ? [filters.employee_id] : [],
        };

        // Abort any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoadingFacets(true);
        try {
            const { data } = await axiosInstance.post(
                `/api/filters/facets?type=${type}`,
                body,
                { signal: abortControllerRef.current.signal }
            );

            if (data?.success && data?.data) {
                const raw = data.data;
                setFacetOptions({
                    departments: Array.isArray(raw.departments) ? raw.departments : [],
                    projects: Array.isArray(raw.projects) ? raw.projects : [],
                    groups: Array.isArray(raw.groups) ? raw.groups : [],
                    employees: Array.isArray(raw.employees) ? raw.employees : [],
                });
            }
        } catch (err) {
            if (axios.isCancel(err) || err.name === "CanceledError" || err.name === "AbortError") return; // Ignore aborted requests
            console.error("[useFilterFacets] Failed to fetch facet options:", err);
        } finally {
            setIsLoadingFacets(false);
        }
    }, [type]);

    // Re-fetch whenever any selected ID changes
    useEffect(() => {
        fetchFacets(selectedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedFilters.department_id,
        selectedFilters.project_id,
        selectedFilters.group_id,
        selectedFilters.employee_id,
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    return { facetOptions, isLoadingFacets };
};

export default useFilterFacets;
